import HttpService from '@services/http-service/http-service';
import {
    DSL_API_PATH,
    DSL_BASE_URL,
    ENABLE_NEW_PREFERRED_DEALER_CONSENT,
} from '@constants';
import { HttpsProxyAgent } from 'https-proxy-agent';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import axios from 'axios';
import AuthenticationService from '@services/authentication-service/authentication-service';
import {
    ConsentsRequest,
    DSLTermsResponse,
    PreferredDealerConsentResponse,
    PrivacyPreferencesRequestData,
} from '@/models/preferred-dealer-consent';
import { PREFERRED_DEALER_CONSENT_CLIMES_FEATURE } from '@constants';

const proxyAgent = new HttpsProxyAgent({
    host: 'internet.ford.com',
    port: 83,
});

export default class PreferredDealerConsentService {
    private httpService: any;
    private appConfig = new AppConfigurationService();

    constructor(httpService: HttpService) {
        this.httpService = httpService;
    }

    private getAzureAdAuthorizationHeaders(): Record<string, string> {
        const authService = new AuthenticationService();
        const accessToken = authService.getAccessToken?.();

        if (!accessToken) {
            return {};
        }

        return {
            Authorization: `Bearer ${accessToken}`,
        };
    }

    public async getPreferredDealerConsentResults(
        countryCode: string
    ): Promise<PreferredDealerConsentResponse | null> {
        const dslUrl = this.appConfig.getAppConfiguration().dslUrl
            ? this.appConfig.getAppConfiguration().dslUrl
            : DSL_BASE_URL;
        const url =
            dslUrl +
            (ENABLE_NEW_PREFERRED_DEALER_CONSENT
                ? DSL_API_PATH.PREFERRED_DEALER_CONSENT
                : `${DSL_API_PATH.PREFERRED_DEALER_CONSENT_LEGACY}?countryCode=${countryCode}`);

        return this.httpService
            .get(`${url}`, false, {
                headers: HttpService.getConsumerKeyAndAuthTokenRequestHeaders(),
                proxy: false,
                httpAgent: proxyAgent,
            })
            .then((response) => {
                if (response.status === 200 && response.data.error === null) {
                    return response.data;
                }

                return null;
            })
            .catch((e) => {
                return e.response.data;
            });
    }

    public async updatePreferredDealerConsent(
        consentsRequestData: ConsentsRequest | PrivacyPreferencesRequestData
    ) {
        const dslUrl = this.appConfig.getAppConfiguration().dslUrl
            ? this.appConfig.getAppConfiguration().dslUrl
            : DSL_BASE_URL;
        const url =
            dslUrl +
            (ENABLE_NEW_PREFERRED_DEALER_CONSENT
                ? DSL_API_PATH.PREFERRED_DEALER_CONSENT
                : DSL_API_PATH.PREFERRED_DEALER_CONSENT_LEGACY);

        return axios
            .post<any>(url, consentsRequestData, {
                headers: {
                    ...HttpService.getConsumerKeyAndAuthTokenRequestHeaders(),
                },
            })
            .then((response) => {
                return response.data;
            })
            .catch((e) => {
                if (e.response?.data) {
                    return e.response.data;
                }
            });
    }

    public async getTermsLegalLanguage(
        brand?: string
    ): Promise<DSLTermsResponse | null> {
        const dslUrl = this.appConfig.getAppConfiguration().dslUrl
            ? this.appConfig.getAppConfiguration().dslUrl
            : DSL_BASE_URL;
        const feature =
            brand?.toLowerCase() === 'lincoln'
                ? PREFERRED_DEALER_CONSENT_CLIMES_FEATURE.lincoln
                : PREFERRED_DEALER_CONSENT_CLIMES_FEATURE.ford;
        const url = `${dslUrl}${DSL_API_PATH.TERMS_AND_CONDITIONS_BY_FEATURE}/${feature}`;
        const authorizationHeaders = this.getAzureAdAuthorizationHeaders();
        const languageISOMap: Record<string, string> = {
            en: 'ENG',
            es: 'SPA',
            fr: 'FRA',
            vi: 'VIE',
            th: 'THA',
        };
        const normalizedLanguageCode = (
            this.appConfig.currentLanguageCode ||
            this.appConfig.currentLanguageRegionCode?.split('-')[0] ||
            this.appConfig.currentThreeLetterLangCode ||
            ''
        )
            .trim()
            .toLowerCase();
        const languageISOCode =
            languageISOMap[normalizedLanguageCode] ||
            this.appConfig.currentThreeLetterLangCode?.toUpperCase() ||
            normalizedLanguageCode.toUpperCase();

        if (!authorizationHeaders.Authorization) {
            return null;
        }

        return axios
            .get<DSLTermsResponse>(url, {
                headers: {
                    ...authorizationHeaders,
                    ...HttpService.getConsumerKeyRequestHeaders(),
                },
                params: languageISOCode ? { languageISOCode } : undefined,
            })
            .then((response) => {
                return response.data;
            })
            .catch((e) => {
                if (e.response?.data) {
                    return e.response.data;
                }
                return null;
            });
    }
}
